import React, { JSX, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import {
  PlusIcon,
  TrashIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { YouTubeStreamSpeedControl } from 'components/YouTubeStreamSpeedControl'
import { useEventDetail } from 'hooks/useEventDetail'
import { useAuth } from 'context/user'
import { useEntitlements } from 'context/plan'
import {
  YouTubeTermsFooter,
  YT_CONNECT_REQUIRED_PREFIX,
  YT_CONSENT_REQUIRED_PREFIX
} from 'components/YouTubeSessionWidget'
import { ProviderBrandIcon, TWITCH_BRAND_ICON_SRC } from 'components/ProviderBrandIcon'
import { YouTubeAreaIconLink } from 'components/YouTubeAreaIconLink'
import { graphqlRequest, withBotName } from 'lib/helpers'
import { isTwitchLoginDeadRefusal, needsLiveCheckConnectionsLink } from 'lib/liveLoginCheckCopy'
import {
  ChannelBinding,
  Connection,
  DeletionResult,
  EventDetail,
  EventYouTubeSourceResult,
  BotInfo
} from 'types/api'
import addChannelMutation from 'graphql/addChannelToEvent.graphql'
import removeChannelMutation from 'graphql/removeChannelFromEvent.graphql'
import listMyConnectionsQuery from 'graphql/listMyConnections.graphql'
import setEventYouTubeSourceMutation from 'graphql/setEventYouTubeSource.graphql'

const SectionCard = ({
  title,
  children,
  action
}: { title: string, children: React.ReactNode, action?: React.ReactNode }): JSX.Element => (
  <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'>
    <div className='mb-4 flex items-center justify-between'>
      <h3 className='text-base font-semibold text-dm-text-primary'>{title}</h3>
      {action}
    </div>
    {children}
  </section>
)

const CONNECT_REQUIRED_PREFIX = 'Connect your Twitch account'
const SCOPE_UPGRADE_PREFIX = 'Your Twitch connection needs updated permissions'

const needsConnectionsLink = (message: string): boolean =>
  message.startsWith(CONNECT_REQUIRED_PREFIX) ||
  message.startsWith(SCOPE_UPGRADE_PREFIX) ||
  message.startsWith(YT_CONNECT_REQUIRED_PREFIX) ||
  message.startsWith(YT_CONSENT_REQUIRED_PREFIX) ||
  needsLiveCheckConnectionsLink(message)

const AddChannelError = ({
  message,
  onDismiss
}: {
  message: string
  onDismiss: () => void
}): JSX.Element => {
  if (needsConnectionsLink(message)) {
    return (
      <ErrorInline onDismiss={onDismiss}>
        {message}{' '}
        <Link
          to='/connections'
          className='font-semibold text-red-200 underline hover:text-red-100'
        >
          Open Connections
        </Link>
      </ErrorInline>
    )
  }
  return <ErrorInline onDismiss={onDismiss}>{message}</ErrorInline>
}

const EdgeTag = ({ edge }: { edge: string | null }): JSX.Element | null => {
  if (edge === 'owner') {
    return (
      <span className='inline-flex shrink-0 items-center rounded-full bg-brand/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand'>
        Your channel
      </span>
    )
  }
  if (edge === 'moderator') {
    return (
      <span className='inline-flex shrink-0 items-center rounded-full bg-[#9146FF]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#9146FF]'>
        You moderate
      </span>
    )
  }
  if (edge === 'superuser') {
    return (
      <span className='inline-flex shrink-0 items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300'>
        Staff add
      </span>
    )
  }
  return null
}

const BotAccessHint = ({
  binding,
  bot
}: {
  binding: ChannelBinding
  bot: BotInfo
}): JSX.Element => (
  <div
    data-testid={`bot-access-hint-${binding.broadcasterUserId}`}
    className='mt-2 flex items-start gap-2 rounded-md border border-[#9146FF]/30 bg-[#9146FF]/5 p-2 text-[11px] leading-relaxed text-dm-text-secondary'
  >
    <ShieldCheckIcon className='mt-0.5 size-4 shrink-0 text-[#9146FF]' aria-hidden='true' />
    <span className='min-w-0'>
      For listening to work, ask {binding.channelLogin}&apos;s owner to type{' '}
      <code className='rounded bg-dm-bg-accent/60 px-1.5 py-0.5 font-mono text-[10px] text-dm-text-primary'>
        /mod {bot.login}
      </code>{' '}
      in their Twitch chat or have them connect FlightDeck themselves.
    </span>
  </div>
)

const connectedTwitchLogin = (connections: Connection[] | null): string | null => {
  if (connections === null) return null
  const twitch = connections.find((c) => c.provider === 'twitch')
  if (twitch === undefined || twitch.status !== 'connected') return null
  return twitch.login
}

const ownUnboundLogin = (
  myTwitchLogin: string | null,
  channels: ChannelBinding[]
): string | null => {
  if (myTwitchLogin === null) return null
  const login = myTwitchLogin.toLowerCase()
  if (channels.some((c) => c.channelLogin.toLowerCase() === login)) return null
  return login
}

export const ADDER_LEFT_LINE =
  'Added by someone who left this stream. When the stream goes live, FlightDeck does not read this channel\'s chat for votes, unless someone in this stream has connected this channel. Chat overlays and emote walls can still show its chat.'
export const HOST_ONLY_ATTACH_NOTE = 'Only the host can attach or remove the host\'s YouTube channel.'

const ChannelsCard = ({
  event,
  bot,
  myTwitchLogin,
  onAdd,
  onRemove,
  onLoginDead
}: {
  event: EventDetail
  bot: BotInfo | null
  myTwitchLogin: string | null
  onAdd: (channelLogin: string) => Promise<void>
  onRemove: (broadcasterUserId: string) => Promise<void>
  onLoginDead: () => void
}): JSX.Element => {
  const [login, setLogin] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { status, featureFor } = useEntitlements()
  const showsLifecycleChrome = status !== 'ready' || featureFor('interactiveContent')

  const isEnded = event.status === 'ENDED'
  const canEdit = !isEnded && bot !== null
  const quickAddLogin = ownUnboundLogin(myTwitchLogin, event.channels)

  const submitAdd = async (trimmed: string): Promise<void> => {
    if (trimmed.length === 0 || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await onAdd(trimmed)
      setLogin('')
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Failed to add channel'
      setError(withBotName(raw, bot !== null ? bot.login : null))
      if (isTwitchLoginDeadRefusal(raw)) onLoginDead()
    } finally {
      setSubmitting(false)
    }
  }

  const handleAdd = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    await submitAdd(login.trim().toLowerCase())
  }

  const handleRemove = async (broadcasterUserId: string): Promise<void> => {
    setRemovingId(broadcasterUserId)
    setError(null)
    try {
      await onRemove(broadcasterUserId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove channel')
    } finally {
      setRemovingId(null)
    }
  }

  return (
    <SectionCard
      title='Twitch chat channels'
      action={
        <span className='inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-brand/15 px-2 text-xs font-semibold text-brand'>
          {event.channels.length}
        </span>
      }
    >
      {isEnded && (
        <p className='mb-4 rounded-md border border-white/10 bg-dm-bg-accent/30 p-3 text-xs text-dm-text-secondary'>
          This event has ended. Its channels are locked. You can&apos;t add or remove channels on a finished event.
        </p>
      )}

      {!isEnded && bot === null && (
        <div
          data-testid='no-environment-bot'
          className='mb-4 flex items-start gap-2 rounded-md border border-amber-500/20 bg-amber-500/5 p-3 text-xs text-amber-300'
        >
          <ExclamationTriangleIcon className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
          <span>
            No Twitch account is connected for this environment yet, so channels can&apos;t be
            added. Ask an administrator to authorize it on the Bots page.
          </span>
        </div>
      )}

      {canEdit && quickAddLogin !== null && (
        <div
          data-testid='own-channel-quick-add'
          className='mb-4 flex items-center justify-between gap-3 rounded-md border border-brand/20 bg-brand/5 px-3 py-2'
        >
          <span className='min-w-0 truncate text-xs text-dm-text-secondary'>
            Your channel:{' '}
            <span className='font-semibold text-dm-text-primary'>{quickAddLogin}</span>
          </span>
          <button
            onClick={() => { void submitAdd(quickAddLogin) }}
            disabled={submitting}
            data-testid='own-channel-quick-add-button'
            className='inline-flex shrink-0 items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
          >
            <PlusIcon className='size-3.5' aria-hidden='true' />
            Add
          </button>
        </div>
      )}

      {canEdit && (
        <form
          onSubmit={(e) => { void handleAdd(e) }}
          className='mb-4 flex flex-col gap-2 sm:flex-row sm:items-center'
          data-testid='add-channel-form'
        >
          <input
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            placeholder='Twitch channel username'
            disabled={submitting}
            className='flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60'
          />
          <button
            type='submit'
            disabled={submitting || login.trim().length === 0}
            className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {submitting ? <LoadingSpinner /> : <PlusIcon className='size-4' aria-hidden='true' />}
            Add
          </button>
        </form>
      )}

      {error !== null && (
        <div className='mb-3'>
          <AddChannelError message={error} onDismiss={() => setError(null)} />
        </div>
      )}

      {event.channels.length === 0
        ? (
          <div className='rounded-lg border border-dashed border-white/10 px-4 py-8 text-center text-sm text-dm-text-secondary'>
            {isEnded
              ? 'This stream ended with no channels bound.'
              : 'No channels bound yet. Add one above to start receiving chat for this stream.'}
          </div>
          )
        : (
          <ul className='space-y-2' data-testid='event-channels-list'>
            {event.channels.map((c: ChannelBinding) => (
              <li
                key={c.broadcasterUserId}
                data-testid={`event-channel-${c.broadcasterUserId}`}
                className='rounded-lg border border-white/5 bg-dm-bg-accent/30 px-3 py-2.5'
              >
                <div className='flex items-center justify-between gap-3'>
                  <div className='flex min-w-0 items-center gap-3'>
                    <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
                    <div className='flex min-w-0 flex-col leading-tight'>
                      <span className='flex min-w-0 items-center gap-2'>
                        <span className='truncate text-sm font-medium text-dm-text-primary'>
                          {c.channelLogin}
                        </span>
                        <EdgeTag edge={c.edge} />
                      </span>
                      <span className='truncate text-[11px] text-dm-text-accent'>
                        Twitch chat
                      </span>
                    </div>
                  </div>
                  {!isEnded && (
                    <button
                      onClick={() => { void handleRemove(c.broadcasterUserId) }}
                      disabled={removingId !== null}
                      title='Remove channel from stream'
                      className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2 py-1 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
                    >
                      {removingId === c.broadcasterUserId
                        ? <LoadingSpinner />
                        : <TrashIcon className='size-3.5' aria-hidden='true' />}
                      Remove
                    </button>
                  )}
                </div>
                {c.botAccess === 'mod_needed' && bot !== null && (
                  <BotAccessHint binding={c} bot={bot} />
                )}

                {c.adderLeft === true && (
                  <p
                    data-testid={`channel-adder-left-${c.broadcasterUserId}`}
                    className='mt-2 rounded-md border border-white/10 bg-dm-bg-accent/30 p-2 text-[11px] leading-relaxed text-dm-text-secondary'
                  >
                    {ADDER_LEFT_LINE}
                  </p>
                )}
              </li>
            ))}
          </ul>
          )}

      {event.status === 'INACTIVE' && showsLifecycleChrome && (
        <p className='mt-3 text-[11px] text-dm-text-accent'>
          Channels start listening when you press Go live.
        </p>
      )}
    </SectionCard>
  )
}

const InviteOnlyChip = (): JSX.Element => (
  <span className='shrink-0 rounded-full bg-dm-bg-accent/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
    Invite-only preview
  </span>
)

export const YouTubeChatCard = ({
  event,
  eventId,
  myConnections,
  onAttachedChange
}: {
  event: EventDetail
  eventId: string
  myConnections: Connection[] | null
  onAttachedChange: (attached: boolean) => void
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const { account, status: entStatus, superuser } = useEntitlements()
  const invited = account?.youtubeChatInvited === true
  const consented = typeof account?.youtubeConsentAt === 'string' && account.youtubeConsentAt.length > 0
  const entReady = entStatus === 'ready' && account !== null
  const youtube = myConnections?.find((c) => c.provider === 'youtube' && c.status === 'connected') ?? null
  const hasYouTube = youtube !== null

  const attached = event.youtubeSourceAttached === true
  const isEnded = event.status === 'ENDED'
  const [attachBusy, setAttachBusy] = useState(false)
  const [attachError, setAttachError] = useState<string | null>(null)

  const handleToggleAttach = async (): Promise<void> => {
    if (auth0Token === undefined || attachBusy) return
    setAttachBusy(true)
    setAttachError(null)
    try {
      const { result } = await graphqlRequest<{ result: EventYouTubeSourceResult }>(
        setEventYouTubeSourceMutation, { eventId, attached: !attached }, auth0Token
      )
      onAttachedChange(result.youtubeSourceAttached)
    } catch (err) {
      setAttachError(err instanceof Error ? err.message : 'Failed to update the YouTube source')
    } finally {
      setAttachBusy(false)
    }
  }

  let body: JSX.Element
  if (!entReady) {
    body = (
      <div className='flex items-center justify-center py-6'>
        <LoadingSpinner />
      </div>
    )
  } else if (!consented || !hasYouTube) {
    body = (
      <div>
        <p className='text-sm text-dm-text-secondary'>
          Your account has YouTube preview access. Review the YouTube terms
          and connect your channel on the Connections page, then come back
          here to attach it to this stream.
        </p>
        <Link
          to='/connections'
          className='mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:text-brand/80'
        >
          Go to Connections
        </Link>
      </div>
    )
  } else {
    body = (
      <div>
        <div
          data-testid='youtube-attach-row'
          className='flex items-center justify-between gap-3 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2.5'
        >
          <div className='min-w-0'>
            <p className='text-sm font-medium text-dm-text-primary'>
              Attach your YouTube channel
            </p>
            <p className='mt-0.5 text-[11px] leading-relaxed text-dm-text-accent'>
              Makes <span className='font-medium text-dm-text-secondary'>{youtube.displayName ?? 'your connected channel'}</span>{' '}
              a source for this stream: it counts toward Go live, and the
              YouTube chat session auto-starts when you press Go live.
            </p>
          </div>
          <button
            type='button'
            onClick={() => { void handleToggleAttach() }}
            disabled={attachBusy || isEnded}
            data-testid='youtube-attach-toggle'
            title={isEnded ? 'This stream has ended, sources are locked' : undefined}
            className='inline-flex shrink-0 items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-semibold text-dm-text-primary hover:border-white/20 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {attachBusy && <LoadingSpinner />}
            {attached ? 'Detach' : 'Attach'}
          </button>
        </div>
        {attachError !== null && (
          <div className='mt-3'>
            <AddChannelError message={attachError} onDismiss={() => setAttachError(null)} />
          </div>
        )}
      </div>
    )
  }

  return (
    <section
      data-testid='youtube-chat-card'
      className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'
    >
      <div className='mb-4 flex items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-3'>
          <YouTubeAreaIconLink size='sm' />
          <h3 className='truncate text-base font-semibold text-dm-text-primary'>YouTube chat</h3>
        </div>
        {entReady && !invited && <InviteOnlyChip />}
      </div>
      {body}
      {superuser && (
        <YouTubeStreamSpeedControl key={eventId} eventId={eventId} ended={isEnded} />
      )}
      <YouTubeTermsFooter />
    </section>
  )
}

export const IntegrationsPage = (): JSX.Element => {
  const { auth0Token } = useAuth()
  const { event, bot, setEvent, refetchEvent, refetchSubscriptions } = useEventDetail()

  const handleAttachedChange = (attached: boolean): void => {
    setEvent((prev) => prev === null ? prev : { ...prev, youtubeSourceAttached: attached })
  }
  const { eventId = '' } = useParams<{ eventId: string }>()

  const [myConnections, setMyConnections] = useState<Connection[] | null>(null)
  const [connectionsReads, setConnectionsReads] = useState(0)
  useEffect(() => {
    if (auth0Token === undefined) return
    let cancelled = false
    const load = async (): Promise<void> => {
      try {
        const resp = await graphqlRequest<{ connections: Connection[] }>(
          listMyConnectionsQuery, {}, auth0Token
        )
        if (!cancelled) setMyConnections(resp.connections ?? [])
      } catch {
      }
    }
    void load()
    return () => { cancelled = true }
  }, [auth0Token, connectionsReads])

  const handleAddChannel = async (channelLogin: string): Promise<void> => {
    if (auth0Token === undefined) return
    const { binding } = await graphqlRequest<{ binding: ChannelBinding }>(
      addChannelMutation, { eventId, channelLogin }, auth0Token
    )
    const alreadyHad = event.channels.some((c) => c.broadcasterUserId === binding.broadcasterUserId)
    setEvent((prev) => {
      if (prev === null) return prev
      return {
        ...prev,
        channels: alreadyHad ? prev.channels : [...prev.channels, binding]
      }
    })
    if (!alreadyHad && event.bots.length === 0) {
      void refetchEvent()
    }
    void refetchSubscriptions()
  }

  const handleRemoveChannel = async (broadcasterUserId: string): Promise<void> => {
    if (auth0Token === undefined) return
    const { result } = await graphqlRequest<{ result: DeletionResult }>(
      removeChannelMutation, { eventId, broadcasterUserId }, auth0Token
    )
    if (result.deleted) {
      void refetchEvent()
      void refetchSubscriptions()
    }
  }

  return (
    <div className='mt-2 flex flex-col gap-1.5 px-4 pb-4'>
      <ChannelsCard
        event={event}
        bot={bot}
        myTwitchLogin={connectedTwitchLogin(myConnections)}
        onAdd={handleAddChannel}
        onRemove={handleRemoveChannel}
        onLoginDead={() => { setConnectionsReads((n) => n + 1) }}
      />
      <YouTubeChatCard
        event={event}
        eventId={eventId}
        myConnections={myConnections}
        onAttachedChange={handleAttachedChange}
      />
    </div>
  )
}
